'use client';

/**
 * Replay handlers for the offline write queue (audit finding OFF-01).
 *
 * Everything the app queues while offline lands in the Dexie `sync_queue` and is
 * drained here on reconnect by `useBackgroundSync`. Before this module existed
 * the drain loop understood exactly one action — `save_comment`, itself a stub
 * that returned `true` without making any server call — and every action the app
 * actually enqueues (the three task-template mutations) fell through to a
 * `default` branch that marked them `failed` with no retry and no user-visible
 * notice. Offline template writes were optimistically shown, promised by the
 * offline banner ("Changes will sync when you reconnect"), and then discarded.
 *
 * Design notes:
 *
 * - A handler returns normally on success. Throwing means "retry later".
 * - Throwing a `PermanentSyncError` means "this can never succeed" (validation
 *   rejected it, the row is gone, the user lost access) — the drain loop
 *   abandons it immediately instead of burning retries on a lost cause.
 * - Handlers deliberately go through the same Supabase client the online path
 *   uses, so RLS, defaults and triggers behave identically to a live write.
 */

import { createClient } from '@/lib/supabase/client';
import {
  createTaskTemplateSchema,
  updateTaskTemplateSchema,
} from '@/lib/validations/task-template';
import { templateInputToRow } from '@/lib/query/hooks/task-template-row';
import { deleteCachedTaskTemplate } from '@/lib/db/offline-db';

/** A failure that retrying cannot fix. */
export class PermanentSyncError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PermanentSyncError';
  }
}

/** Human-readable label for an action, used in the "couldn't sync" message. */
export const SYNC_ACTION_LABELS: Record<string, string> = {
  createTaskTemplate: 'New task template',
  updateTaskTemplate: 'Task template edit',
  deleteTaskTemplate: 'Task template deletion',
};

export function describeSyncAction(action: string): string {
  return SYNC_ACTION_LABELS[action] ?? action;
}

type SyncHandler = (payload: Record<string, unknown>) => Promise<void>;

/**
 * Postgres error codes that will never succeed on retry: constraint violations,
 * bad input, and RLS rejections. Anything else (network blips, 5xx, timeouts)
 * is treated as transient.
 */
const PERMANENT_PG_CODES = new Set([
  '22P02', // invalid text representation
  '23502', // not-null violation
  '23503', // foreign key violation
  '23505', // unique violation
  '23514', // check violation
  '42501', // insufficient privilege (RLS)
  'PGRST116', // no rows returned where one was required
]);

function raiseSupabaseError(
  error: { message: string; code?: string },
  context: string,
): never {
  const message = `${context}: ${error.message}`;
  if (error.code && PERMANENT_PG_CODES.has(error.code)) {
    throw new PermanentSyncError(message);
  }
  throw new Error(message);
}

const handlers: Record<string, SyncHandler> = {
  async createTaskTemplate(payload) {
    const workspaceId = payload.workspaceId as string | undefined;
    const parsed = createTaskTemplateSchema.safeParse(payload.data);
    if (!workspaceId || !parsed.success) {
      throw new PermanentSyncError(
        parsed.success
          ? 'Queued template is missing its workspace.'
          : (parsed.error.issues[0]?.message ?? 'Queued template is invalid.'),
      );
    }

    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    // No session yet — the user may still be signing back in. Transient.
    if (!user) throw new Error('Not signed in; will retry.');

    const { error } = await supabase.from('task_templates').insert({
      workspace_id: workspaceId,
      created_by: user.id,
      ...templateInputToRow(parsed.data),
    });

    if (error) raiseSupabaseError(error, 'Failed to create task template');
  },

  async updateTaskTemplate(payload) {
    const templateId = payload.templateId as string | undefined;
    const parsed = updateTaskTemplateSchema.safeParse(payload.data);
    if (!templateId || !parsed.success) {
      throw new PermanentSyncError(
        parsed.success
          ? 'Queued template edit is missing its template id.'
          : (parsed.error.issues[0]?.message ?? 'Queued template edit is invalid.'),
      );
    }

    const row = templateInputToRow(parsed.data);
    if (Object.keys(row).length === 0) return; // nothing to send

    const supabase = createClient();
    const { data, error } = await supabase
      .from('task_templates')
      .update(row)
      .eq('id', templateId)
      .select('id');

    if (error) raiseSupabaseError(error, 'Failed to update task template');

    // Zero rows means the template was deleted (or is no longer visible to this
    // user) while the edit sat in the queue. Retrying will never help.
    if (!data || data.length === 0) {
      throw new PermanentSyncError(
        'The template was deleted before this edit could sync.',
      );
    }
  },

  async deleteTaskTemplate(payload) {
    const templateId = payload.templateId as string | undefined;
    if (!templateId) {
      throw new PermanentSyncError('Queued deletion is missing its template id.');
    }

    const supabase = createClient();
    const { error } = await supabase
      .from('task_templates')
      .delete()
      .eq('id', templateId);

    if (error) raiseSupabaseError(error, 'Failed to delete task template');

    // A delete that matched nothing is already in the desired state — success.
    await deleteCachedTaskTemplate(templateId).catch(() => {});
  },
};

/**
 * Replays one queued item. Resolves on success; throws to request a retry, or
 * throws `PermanentSyncError` to abandon it.
 *
 * An unrecognised action is permanent by definition — no future version of this
 * running bundle will grow a handler for it.
 */
export async function replaySyncAction(
  action: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const handler = handlers[action];
  if (!handler) {
    throw new PermanentSyncError(`No sync handler for action "${action}".`);
  }
  await handler(payload);
}

export function hasSyncHandler(action: string): boolean {
  return action in handlers;
}
